import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listFacilityRoutingConfig,
  updateFacilityRoutingConfig,
  listRoutingDecisions,
  REGION_CODES,
  type RegionCode,
} from "@/lib/routing.functions";
import { listTenants } from "@/lib/admin-config.functions";
import { useAuth, tenantIdsWithRole } from "@/hooks/use-auth";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

export const Route = createFileRoute("/_authenticated/app/admin/routing")({
  component: RoutingAdminPage,
});

const REGION_LABEL: Record<RegionCode, string> = {
  NE: "Northeast",
  SE: "Southeast",
  MW: "Midwest",
  SW: "Southwest",
  W: "West",
  INTL: "International",
};

function RoutingAdminPage() {
  const { roles } = useAuth();
  const { isSuperAdmin: su } = useScope();
  const tenantIds = useMemo(() => {
    if (su) return null;
    return tenantIdsWithRole(roles, "tenant_admin");
  }, [roles, su]);

  const fetchTenants = useServerFn(listTenants);
  const { data: tenants = [] } = useQuery({
    queryKey: ["admin", "routing", "tenants"],
    queryFn: () => fetchTenants(),
  });
  const allowed = useMemo(
    () => (tenantIds === null ? tenants : tenants.filter((t: any) => tenantIds.includes(t.id))),
    [tenants, tenantIds],
  );
  const [tenantId, setTenantId] = useState<string>("");
  if (!tenantId && allowed.length) setTenantId(allowed[0].id);

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Multi-Node Routing</h1>
          <p className="text-sm text-muted-foreground">
            Deterministic routing policy: inventory → eligibility → region preference → facility priority.
          </p>
        </div>
        <Select value={tenantId} onValueChange={setTenantId}>
          <SelectTrigger className="w-[280px]"><SelectValue placeholder="Tenant" /></SelectTrigger>
          <SelectContent>
            {allowed.map((t: any) => (
              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </header>

      {tenantId ? (
        <Tabs defaultValue="facilities">
          <TabsList>
            <TabsTrigger value="facilities">Facility config</TabsTrigger>
            <TabsTrigger value="decisions">Routing decisions</TabsTrigger>
          </TabsList>
          <TabsContent value="facilities" className="mt-4">
            <FacilityConfig tenantId={tenantId} />
          </TabsContent>
          <TabsContent value="decisions" className="mt-4">
            <DecisionsViewer tenantId={tenantId} />
          </TabsContent>
        </Tabs>
      ) : (
        <p className="text-sm text-muted-foreground">Select a tenant to configure routing.</p>
      )}
    </div>
  );
}

function FacilityConfig({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const listFn = useServerFn(listFacilityRoutingConfig);
  const saveFn = useServerFn(updateFacilityRoutingConfig);
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin", "routing", "facilities", tenantId],
    queryFn: () => listFn({ data: { tenantId } }),
  });
  const save = useMutation({
    mutationFn: (vars: { facilityId: string; preferred_regions: RegionCode[]; routing_priority: number; routing_eligible: boolean }) =>
      saveFn({ data: vars }),
    onSuccess: () => {
      toast.success("Routing config saved");
      qc.invalidateQueries({ queryKey: ["admin", "routing", "facilities", tenantId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Save failed"),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading facilities…</p>;
  if (!rows.length) return <p className="text-sm text-muted-foreground">No facilities for this tenant.</p>;

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Lower priority value wins. Region chips mark which destination regions a facility prefers.
        Region match outranks priority; full stock outranks region.
      </p>
      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left">
            <tr>
              <th className="px-3 py-2">Facility</th>
              <th className="px-3 py-2">Eligible</th>
              <th className="px-3 py-2 w-24">Priority</th>
              <th className="px-3 py-2">Preferred regions</th>
              <th className="px-3 py-2 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f: any) => (
              <FacilityRow key={f.id} row={f} onSave={(v) => save.mutate({ facilityId: f.id, ...v })} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FacilityRow({ row, onSave }: { row: any; onSave: (v: { preferred_regions: RegionCode[]; routing_priority: number; routing_eligible: boolean }) => void }) {
  const [regions, setRegions] = useState<RegionCode[]>(row.preferred_regions ?? []);
  const [priority, setPriority] = useState<number>(row.routing_priority ?? 100);
  const [eligible, setEligible] = useState<boolean>(row.routing_eligible ?? true);
  const toggle = (r: RegionCode) =>
    setRegions((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
  return (
    <tr className="border-t">
      <td className="px-3 py-2 font-medium">{row.name}</td>
      <td className="px-3 py-2">
        <Switch checked={eligible} onCheckedChange={setEligible} />
      </td>
      <td className="px-3 py-2">
        <Input type="number" min={1} max={999} value={priority}
          onChange={(e) => setPriority(Number(e.target.value) || 100)}
          className="h-8 w-20" />
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1.5">
          {REGION_CODES.map((r) => {
            const active = regions.includes(r);
            return (
              <button key={r} type="button" onClick={() => toggle(r)}
                className={`rounded-md border px-2 py-0.5 text-xs transition ${active ? "border-primary bg-primary/10 text-primary" : "border-border bg-background text-muted-foreground hover:bg-muted"}`}>
                {r} <span className="text-[10px] opacity-60">{REGION_LABEL[r]}</span>
              </button>
            );
          })}
        </div>
      </td>
      <td className="px-3 py-2 text-right">
        <Button size="sm" variant="outline"
          onClick={() => onSave({ preferred_regions: regions, routing_priority: priority, routing_eligible: eligible })}>
          Save
        </Button>
      </td>
    </tr>
  );
}

function DecisionsViewer({ tenantId }: { tenantId: string }) {
  const listFn = useServerFn(listRoutingDecisions);
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin", "routing", "decisions", tenantId],
    queryFn: () => listFn({ data: { tenantId, limit: 50 } }),
  });
  if (isLoading) return <p className="text-sm text-muted-foreground">Loading decisions…</p>;
  if (!rows.length) return <p className="text-sm text-muted-foreground">No routing decisions yet.</p>;
  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left">
          <tr>
            <th className="px-3 py-2">When</th>
            <th className="px-3 py-2">Order</th>
            <th className="px-3 py-2">Dest</th>
            <th className="px-3 py-2">Chosen facility</th>
            <th className="px-3 py-2">Flags</th>
            <th className="px-3 py-2">Reasons</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r: any) => (
            <tr key={r.id} className="border-t align-top">
              <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
              </td>
              <td className="px-3 py-2 font-mono text-xs">{r.order_id.slice(0, 8)}</td>
              <td className="px-3 py-2">
                <Badge variant="outline">{r.destination_region ?? "—"}</Badge>
                <div className="text-[11px] text-muted-foreground">{r.destination_postal_code ?? ""}</div>
              </td>
              <td className="px-3 py-2">{r.chosen_facility_name ?? <span className="text-muted-foreground">none</span>}</td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  {r.region_matched && <Badge>region</Badge>}
                  {r.fully_stocked ? <Badge variant="secondary">full</Badge> : <Badge variant="destructive">partial</Badge>}
                  {r.backordered && <Badge variant="destructive">backorder</Badge>}
                </div>
              </td>
              <td className="px-3 py-2 text-xs text-muted-foreground">
                {(r.reason_codes ?? []).join(", ")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
